/**
 * The reads the Intake group needs that nothing else on the page already
 * fetches.
 *
 * Appointments are deliberately NOT fetched here — they arrive from
 * `appointmentsContext` on `TimelineContext`, which is already loaded once per
 * page for the procedure list and the header's offering pill. A fourth
 * Appointment query per patient view would be pure duplication.
 */

import { getReferenceString, resolveId } from '@medplum/core';
import { Communication, Patient, Practitioner, QuestionnaireResponse } from '@medplum/fhirtypes';
import { useMedplum } from '@medplum/react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { ActiveLeadOverride, parseActiveLeadOverride } from '@bariendo/lib/active-lead';
import { isPnConsultationAnswers, PatientAccessCallAnswers } from '@bariendo/lib/patient-access-note';
import { getPnConsultationOutcome } from '@bariendo/lib/pn-consultation-note';
import { BARIENDO_CT_SYSTEM, FlagCategory } from '@bariendo/lib/constants';
import { resolveActiveCoverage, searchBenefitEventResources } from '@bariendo/lib/benefit-events';
import { RecordedCoverageAction, recordedCoverageActions } from '@bariendo/lib/payment-status';
import { reportError } from '@/services/error-reporting';
import { canSearchResourceType } from '@/services/permissions';
import { CONSULTATION_NOTE_CATEGORY_TOKEN, readSavedCallNote } from './services/introCallNote';
import { deriveSignupDate } from './services/signupDate';
import { ELIGIBILITY_QUESTIONNAIRE_URL, readEligibilityIntakeSelections } from '@bariendo/lib/eligibility-intake';

/** The consult "next step" form — `packages/bots/data/questionnaire/next-step.json`. */
const NEXT_STEP_QUESTIONNAIRE_URL = 'http://bariendo.com/fhir/Questionnaire/next-step';

export interface IntakeBillingData {
  /** Signup date. No creation field exists on Patient; the episode's start is
   *  the repo's established stand-in, and every patient gets one as of v21. */
  signupDate?: string;
  /** The newest saved consultation call note's answers — PAC intro call or PN consultation. Package
   *  and Location fall back to the
   *  `procedure` and `loc` beats when no Patient extension is set yet. Both
   *  scripts record those under the same ids. */
  introCallAnswers?: PatientAccessCallAnswers;
  /** The outcome on the newest saved PN consultation note: a PN consult's result, which the
   *  `next-step` form (the NP consult's) never carries. */
  pnConsultOutcome?: string;
  /** Last resort for Service: what the lead picked on their eligibility form.
   *  Without it the row was blank for every lead with no intro call charted yet. */
  eligibilityProcedure?: string;
  /** Last resort for Location: the market the lead's eligibility form resolved
   *  from their ZIP. Same reason as Service above — the row was blank for every
   *  lead with no intro call charted yet (BAR-2597). */
  eligibilityMarket?: string;
  /** Miles from the lead's ZIP to that market. Shown only when the market came
   *  from the form rather than a rep: bariendo-be no longer applies a radius
   *  cutoff, so "Boston" alone would read as local for a lead 340 miles away. */
  eligibilityDistanceMi?: number;
  consultOutcomes: string[];
  /** The employer or insurance steps recorded on the active Coverage, for the dates group. */
  coverageActions: RecordedCoverageAction[];
  /** Newest `Communication.sent`, for the Active-lead clock. */
  lastCommunicationSent?: string;
  /** The live manual override, if a rep has parked this lead. */
  override?: ActiveLeadOverride;
  /** Display name behind `override.author`, for the reason-line avatar. */
  overrideAuthorName?: string;
  loading: boolean;
}

export interface IntakeBillingResult extends IntakeBillingData {
  /** Re-reads after a write — the Active-lead toggle needs the new Flag. */
  refresh: () => Promise<void>;
}

const EMPTY: IntakeBillingData = { consultOutcomes: [], coverageActions: [], loading: false };

const COVERAGE_TYPES = ['Claim', 'ClaimResponse', 'Coverage', 'EpisodeOfCare'] as const;

/**
 * The coverage steps on the patient's active Coverage, as the payment status reads them. Apart from
 * the main read, and asked only of a role whose policy can search every type it touches: a search
 * naming a type outside the policy is refused, and one refusal must not blank the whole panel.
 */
async function readCoverageActions(
  medplum: ReturnType<typeof useMedplum>,
  patientId: string
): Promise<RecordedCoverageAction[]> {
  if (!COVERAGE_TYPES.every((type) => canSearchResourceType(medplum, type))) return [];

  try {
    const [resources, coverage] = await Promise.all([
      searchBenefitEventResources(medplum, patientId),
      resolveActiveCoverage(medplum, patientId),
    ]);
    return recordedCoverageActions(resources, coverage);
  } catch (error) {
    reportError(error, { component: 'useIntakeBilling' });
    return [];
  }
}

/** Every answer the multi-select `next-step` item carries, newest response first. */
function readNextStepAnswers(responses: QuestionnaireResponse[]): string[] {
  const latest = responses[0];
  if (!latest) return [];

  const item = latest.item?.find((entry) => entry.linkId === 'next-step');

  return (item?.answer ?? [])
    .map((answer) => answer.valueCoding?.display ?? answer.valueCoding?.code ?? answer.valueString)
    .filter((value): value is string => Boolean(value));
}

function readIntroCall(notes: Communication[]): Pick<IntakeBillingData, 'introCallAnswers' | 'pnConsultOutcome'> {
  const saved = readSavedCallNote(notes);
  if (!saved) return {};

  const pnConsultOutcome = isPnConsultationAnswers(saved.answers) ? getPnConsultationOutcome(saved.answers) : undefined;
  return { introCallAnswers: saved.answers, ...(pnConsultOutcome ? { pnConsultOutcome } : {}) };
}

export function useIntakeBilling(patient: Patient | undefined): IntakeBillingResult {
  const medplum = useMedplum();
  const [data, setData] = useState<IntakeBillingData>(EMPTY);
  // Only the newest read lands: the page does not remount on a patient switch, so the previous
  // patient's reads, still in flight, must not draw their panel under the next patient.
  const latestRead = useRef(0);
  const loadedFor = useRef<string | undefined>(undefined);

  const load = useCallback(async () => {
    const sequence = ++latestRead.current;
    if (!patient?.id) {
      setData(EMPTY);
      return;
    }

    const subject = getReferenceString(patient);
    // A re-read for the same patient keeps what is on screen; a different patient starts empty, or
    // the last patient's lead status and dates would read as this one's until the reads land.
    const samePatient = loadedFor.current === patient.id;
    loadedFor.current = patient.id;
    setData((previous) => (samePatient ? { ...previous, loading: true } : { ...EMPTY, loading: true }));

    try {
      const [episodes, notes, latestCommunication, responses, eligibility, flags, coverageActions] = await Promise.all([
        medplum.searchResources('EpisodeOfCare', { patient: subject, _count: 10, _sort: 'date' }),
        // Filtered server-side by category rather than by recency. The note is
        // written once, early, so any patient with real SMS or call history
        // pushes it out of a newest-N window — silently taking the Outcome row
        // and the Service/Location fallback with it. The category carries both
        // the PAC intro call and the PN consultation (BAR-2590).
        //
        // Saved notes only. Every abandoned attempt at either card leaves an
        // `in-progress` draft, and two cards' worth of drafts would push the one
        // saved note out of the same small window — `pickSavedCallNote` never
        // uses a draft anyway.
        medplum.searchResources('Communication', {
          subject,
          category: CONSULTATION_NOTE_CATEGORY_TOKEN,
          status: 'completed',
          _count: 5,
          _sort: '-sent',
        }),
        // Newest communication of any kind — the Active-lead clock. Separate
        // from the note read above, which is category-filtered and would
        // otherwise date "last contact" to the intro call forever.
        medplum.searchResources('Communication', { subject, _count: 1, _sort: '-sent' }),
        medplum.searchResources('QuestionnaireResponse', {
          subject,
          questionnaire: NEXT_STEP_QUESTIONNAIRE_URL,
          _count: 5,
          _sort: '-authored',
        }),
        // The eligibility submission. Separate search rather than one unfiltered
        // read of both forms: a lead with several next-step responses would push
        // the eligibility one out of any newest-N window, which is the same trap
        // the intro-call note read above documents.
        medplum.searchResources('QuestionnaireResponse', {
          subject,
          questionnaire: ELIGIBILITY_QUESTIONNAIRE_URL,
          _count: 1,
          _sort: '-authored',
        }),
        medplum.searchResources('Flag', {
          subject,
          category: `${BARIENDO_CT_SYSTEM}|${FlagCategory.PAC_LEAD_STATUS}`,
          _count: 5,
          // The upsert should keep this to one, but two reps toggling at once
          // could race it. Newest wins rather than whichever came back first.
          _sort: '-_lastUpdated',
        }),
        readCoverageActions(medplum, patient.id),
      ]);

      const override = flags.map(parseActiveLeadOverride).find(Boolean);
      // Read once. This was calling `readEligibilityIntakeSelections` twice for
      // one field; it now supplies three.
      const eligibilitySelections = readEligibilityIntakeSelections(eligibility[0]);
      const overrideAuthorName = await readAuthorName(medplum, override);
      if (sequence !== latestRead.current) return;

      setData({
        signupDate: deriveSignupDate(episodes, eligibility[0]),
        ...readIntroCall(notes),
        consultOutcomes: readNextStepAnswers(responses),
        coverageActions,
        ...(eligibilitySelections.procedure ? { eligibilityProcedure: eligibilitySelections.procedure } : {}),
        ...(eligibilitySelections.market ? { eligibilityMarket: eligibilitySelections.market } : {}),
        // Against null, not falsy: zero miles is a real answer for a lead inside
        // a market.
        ...(eligibilitySelections.distanceMi != null
          ? { eligibilityDistanceMi: eligibilitySelections.distanceMi }
          : {}),
        lastCommunicationSent: latestCommunication[0]?.sent,
        override,
        overrideAuthorName,
        loading: false,
      });
    } catch (error) {
      // Keep whatever is already on screen rather than blanking the section. A
      // stale panel is recoverable; an empty one on a transient failure looks
      // like the patient has no history. Silence was the other half of the
      // problem, so the failure is reported rather than swallowed.
      reportError(error, { component: 'useIntakeBilling' });
      if (sequence === latestRead.current) setData((previous) => ({ ...previous, loading: false }));
    }
  }, [medplum, patient]);

  useEffect(() => {
    load();
  }, [load]);

  return { ...data, refresh: load };
}

/**
 * The parked-by practitioner's display name, for the reason-line avatar.
 *
 * A failure here must not lose the override — the reason line still reads
 * correctly without an avatar, and dropping the whole row because a name would
 * not resolve is the worse outcome.
 */
async function readAuthorName(
  medplum: ReturnType<typeof useMedplum>,
  override: ActiveLeadOverride | undefined
): Promise<string | undefined> {
  const reference = override?.author?.reference;
  if (!reference) return undefined;

  const id = resolveId(override.author);
  if (!id) return undefined;

  try {
    const practitioner = await medplum.readResource('Practitioner', id);

    return formatPractitionerName(practitioner);
  } catch {
    return undefined;
  }
}

function formatPractitionerName(practitioner: Practitioner): string | undefined {
  const name = practitioner.name?.[0];
  if (!name) return undefined;

  return [name.given?.[0], name.family].filter(Boolean).join(' ') || undefined;
}
